/**
 * The in-person scorekeeper.
 *
 * This is the mode with no dice in it. Six real dice are already on the table
 * and the people around it have already agreed what the throw was worth — the
 * only job here is arithmetic, turn order, and not losing the game.
 *
 * So it deliberately does NOT validate scores. You can enter 1,250 for a hand
 * that cannot be worth 1,250, because every table plays a slightly different
 * rulebook and a scorekeeper that argues with the people holding the dice is a
 * scorekeeper that gets closed. The reference card is a reference, not a check.
 *
 * The one thing it takes seriously is durability: the whole game is mirrored to
 * localStorage on every change, because a phone that locks in the middle of a
 * four-player game to 10,000 must not lose it.
 */

import { useEffect, useState } from 'react';
import { RULE_CARD } from '@shared/scoring';
import { Check, Eyebrow, Field, Masthead, Opt, Page, cx, fmt } from '../ui/shell';

const QUICK = [50, 100, 150, 200, 300, 500, 1000, 1500];
const SAVE_KEY = 'farkle:keeper:v1';

interface Keeper {
  name: string;
  score: number;
  onBoard: boolean;
  turns: number[];
}

interface Saved {
  phase: 'play' | 'over';
  players: Keeper[];
  current: number;
  round: number;
  closerIdx: number | null;
  target: number;
  threshold: number;
  finalRound: boolean;
}

function load(): Saved | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Saved;
    if (!Array.isArray(data.players) || data.players.length < 2) return null;
    return data;
  } catch {
    return null;
  }
}

function save(data: Saved | null) {
  try {
    if (data) localStorage.setItem(SAVE_KEY, JSON.stringify(data));
    else localStorage.removeItem(SAVE_KEY);
  } catch {
    /* private mode: the app works, it just forgets */
  }
}

export function Scorekeeper({ onExit }: { onExit: () => void }) {
  const resumable = useState(load)[0];

  const [phase, setPhase] = useState<'setup' | 'play' | 'over'>('setup');
  const [names, setNames] = useState<string[]>(['', '']);
  const [target, setTarget] = useState(10_000);
  const [threshold, setThreshold] = useState(500);
  const [finalRound, setFinalRound] = useState(true);

  const [players, setPlayers] = useState<Keeper[]>([]);
  const [current, setCurrent] = useState(0);
  const [round, setRound] = useState(1);
  const [closerIdx, setCloserIdx] = useState<number | null>(null);

  const [adds, setAdds] = useState<number[]>([]);
  const [digits, setDigits] = useState('');
  const [mode, setMode] = useState<'quick' | 'keypad'>('quick');
  const [stamped, setStamped] = useState(false);
  const [history, setHistory] = useState<Saved[]>([]);
  const [showRules, setShowRules] = useState(false);

  const turnPoints = mode === 'quick' ? adds.reduce((a, b) => a + b, 0) : Number(digits || 0);
  const me = players[current];
  const needsOpener = !!me && !me.onBoard && turnPoints < threshold;
  const canBank = !!me && turnPoints > 0 && !needsOpener;

  // Mirror to storage on every change. Cheap, and it is the difference between a
  // locked phone being an inconvenience and being the end of the game.
  useEffect(() => {
    if (phase === 'setup') return;
    save({ phase, players, current, round, closerIdx, target, threshold, finalRound });
  }, [phase, players, current, round, closerIdx, target, threshold, finalRound]);

  const clearTurn = () => {
    setAdds([]);
    setDigits('');
    setStamped(false);
  };

  const snapshot = (): Saved => ({
    phase: phase === 'setup' ? 'play' : phase,
    players: players.map((p) => ({ ...p, turns: [...p.turns] })),
    current,
    round,
    closerIdx,
    target,
    threshold,
    finalRound,
  });

  const restore = (s: Saved) => {
    setPhase(s.phase);
    setPlayers(s.players);
    setCurrent(s.current);
    setRound(s.round);
    setCloserIdx(s.closerIdx);
    setTarget(s.target);
    setThreshold(s.threshold);
    setFinalRound(s.finalRound);
  };

  const setCount = (n: number) => setNames(Array.from({ length: n }, (_, i) => names[i] ?? ''));

  const startGame = () => {
    setPlayers(
      names.map((n, i) => ({
        name: n.trim() || `Player ${i + 1}`,
        score: 0,
        onBoard: threshold === 0,
        turns: [],
      })),
    );
    setCurrent(0);
    setRound(1);
    setCloserIdx(null);
    setHistory([]);
    clearTurn();
    setPhase('play');
  };

  const advance = (nextPlayers: Keeper[], closer: number | null) => {
    const nextIdx = (current + 1) % nextPlayers.length;
    if (closer !== null && nextIdx === closer) {
      setPlayers(nextPlayers);
      setPhase('over');
      return;
    }
    setPlayers(nextPlayers);
    setCurrent(nextIdx);
    if (nextIdx === 0) setRound((r) => r + 1);
    clearTurn();
  };

  const bank = () => {
    if (!canBank) return;
    setHistory((h) => [...h.slice(-40), snapshot()]);
    const next = players.map((p, i) =>
      i === current
        ? { ...p, score: p.score + turnPoints, onBoard: true, turns: [...p.turns, turnPoints] }
        : p,
    );
    let closer = closerIdx;
    if (closer === null && next[current]!.score >= target) {
      if (!finalRound) {
        setPlayers(next);
        setPhase('over');
        return;
      }
      closer = current;
      setCloserIdx(current);
    }
    advance(next, closer);
  };

  const farkle = () => {
    setHistory((h) => [...h.slice(-40), snapshot()]);
    setStamped(true);
    const next = players.map((p, i) => (i === current ? { ...p, turns: [...p.turns, 0] } : p));
    // Long enough to read the stamp, short enough that nobody reaches for the phone.
    window.setTimeout(() => advance(next, closerIdx), 620);
  };

  const undo = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    restore(prev);
    setHistory((h) => h.slice(0, -1));
    clearTurn();
  };

  const switchMode = (m: 'quick' | 'keypad') => {
    if (m === mode) return;
    // Carry the number across rather than dropping it: switching input method
    // mid-entry should not silently discard what you already counted.
    if (m === 'keypad') setDigits(turnPoints ? String(turnPoints) : '');
    else setAdds(turnPoints ? [turnPoints] : []);
    setMode(m);
  };

  const playAgain = () => {
    setPlayers((ps) => ps.map((p) => ({ ...p, score: 0, onBoard: threshold === 0, turns: [] })));
    setCurrent(0);
    setRound(1);
    setCloserIdx(null);
    setHistory([]);
    clearTurn();
    setPhase('play');
  };

  const quitToMenu = () => {
    save(null);
    onExit();
  };

  // ── setup ────────────────────────────────────────────────────────────────

  if (phase === 'setup') {
    return (
      <Page>
        <Masthead sub="six-dice scorekeeper" />

        {resumable && (
          <div className="well resume">
            <Eyebrow tone="dim">Unfinished game</Eyebrow>
            <div className="resume-who">
              {resumable.players.map((p) => p.name).join(', ')} — round {resumable.round}
            </div>
            <button type="button" className="btn btn--primary resume-btn" onClick={() => restore(resumable)}>
              PICK IT BACK UP
            </button>
          </div>
        )}

        <Field label="How many players">
          <div className="opt-row">
            {[2, 3, 4, 5, 6, 7, 8].map((n) => (
              <Opt key={n} on={names.length === n} onClick={() => setCount(n)} label={`${n} players`}>
                {n}
              </Opt>
            ))}
          </div>
        </Field>

        <Field label="Who's playing">
          <div className="name-list">
            {names.map((n, i) => (
              <div className="name-row" key={i}>
                <span className="idx">{i + 1}</span>
                <input
                  className="input"
                  value={n}
                  onChange={(e) => setNames(names.map((v, j) => (j === i ? e.target.value : v)))}
                  placeholder={`Player ${i + 1}`}
                  maxLength={14}
                  aria-label={`Player ${i + 1} name`}
                />
              </div>
            ))}
          </div>
        </Field>

        <Field label="Play to">
          <div className="opt-row">
            {[2000, 5000, 10_000, 20_000].map((t) => (
              <Opt key={t} wide on={target === t} onClick={() => setTarget(t)}>
                {fmt(t)}
              </Opt>
            ))}
          </div>
        </Field>

        <Field label="Points needed to get on the board">
          <div className="opt-row">
            {[0, 350, 500, 1000].map((t) => (
              <Opt key={t} wide on={threshold === t} onClick={() => setThreshold(t)}>
                {t === 0 ? 'None' : t}
              </Opt>
            ))}
          </div>
        </Field>

        <Check on={finalRound} onClick={() => setFinalRound(!finalRound)}>
          Everyone else gets one last turn once someone hits the target
        </Check>

        <div className="spacer" />
        <div className="btn-stack">
          <button type="button" className="btn btn--primary" onClick={startGame}>
            START GAME
          </button>
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            Back
          </button>
        </div>
      </Page>
    );
  }

  // ── game over ────────────────────────────────────────────────────────────

  if (phase === 'over') {
    const rankedList = [...players].sort((a, b) => b.score - a.score);
    const top = rankedList[0]?.score ?? 0;
    const winners = rankedList.filter((p) => p.score === top);

    return (
      <Page>
        <div className="rise">
          <Masthead sub={winners.length > 1 ? 'dead heat' : 'final tally'} />

          <div className="card center winner">
            <Eyebrow tone="blood">{winners.length > 1 ? 'Tied at the top' : 'Winner'}</Eyebrow>
            <div className="winner-name">{winners.map((w) => w.name).join(' & ')}</div>
            <div className="num winner-score">{fmt(top)}</div>
          </div>

          <div className="standings final-standings">
            {rankedList.map((p, i) => (
              <div className="row" key={p.name + i}>
                <span className="rank">{i + 1}</span>
                <span className="who">{p.name}</span>
                <span className="score">{fmt(p.score)}</span>
              </div>
            ))}
          </div>

          <div className="btn-stack">
            <button type="button" className="btn" onClick={undo} disabled={!history.length}>
              Undo last turn
            </button>
            <button type="button" className="btn btn--primary" onClick={playAgain}>
              PLAY AGAIN — SAME PLAYERS
            </button>
            <button type="button" className="btn btn--ghost" onClick={quitToMenu}>
              Done
            </button>
          </div>
        </div>
      </Page>
    );
  }

  // ── play ─────────────────────────────────────────────────────────────────

  if (!me) return null;
  const leadScore = Math.max(...players.map((p) => p.score));
  const others = players.map((p, i) => ({ ...p, i })).filter((p) => p.i !== current);

  return (
    <Page>
      <div className="topbar">
        <div>
          <Eyebrow>Round {round}</Eyebrow>
          <div className="meta">to {fmt(target)}</div>
        </div>
        <div className="btn-row">
          <button type="button" className="btn btn--small" onClick={() => setShowRules(!showRules)}>
            Scoring
          </button>
          <button type="button" className="btn btn--small" onClick={undo} disabled={!history.length}>
            Undo
          </button>
          <button type="button" className="btn btn--small" onClick={quitToMenu}>
            Quit
          </button>
        </div>
      </div>

      {showRules && (
        <div className="well--dark rise rule-sheet">
          <Eyebrow className="field-label">Common scoring</Eyebrow>
          <div className="rules">
            {RULE_CARD.map(([what, worth]) => (
              <div key={what}>
                <span>{what}</span>
                <span>{worth}</span>
              </div>
            ))}
          </div>
          <div className="fineprint">
            House rules vary — this is a reference only. The scorekeeper adds whatever you enter.
          </div>
        </div>
      )}

      {closerIdx !== null && (
        <div className="banner">
          <Eyebrow tone="ivory">{players[closerIdx]?.name} is out — last turns</Eyebrow>
        </div>
      )}

      <div className="card">
        {stamped && (
          <div className="stamp-wrap">
            <div className="stamp is-stamped">FARKLE</div>
          </div>
        )}

        <div className="turn-head">
          <div className="turn-name">{me.name}</div>
          <div className="turn-total">
            <Eyebrow tone="ink">Total</Eyebrow>
            <div className="num">{fmt(me.score)}</div>
          </div>
        </div>

        <div className="rule" />

        <div className="turn-body">
          <Eyebrow tone="ink">This turn</Eyebrow>
          <div className="turn-points">{fmt(turnPoints)}</div>
          <div className={cx('turn-note', needsOpener && 'turn-note--warn')}>
            {needsOpener
              ? `needs ${fmt(threshold)} to get on the board`
              : turnPoints > 0
                ? `banks to ${fmt(me.score + turnPoints)}`
                : ''}
          </div>
        </div>
      </div>

      <div className="spacer" />

      <div className="btn-row seg">
        {(['quick', 'keypad'] as const).map((m) => (
          <button
            key={m}
            type="button"
            className={cx('seg-btn', mode === m && 'seg-btn--on')}
            onClick={() => switchMode(m)}
            aria-pressed={mode === m}
          >
            {m === 'quick' ? 'Quick add' : 'Type it'}
          </button>
        ))}
      </div>

      {mode === 'quick' ? (
        <div className="quick-grid">
          {QUICK.map((q) => (
            <button type="button" key={q} className="chip" onClick={() => setAdds([...adds, q])}>
              +{q}
            </button>
          ))}
        </div>
      ) : (
        <div className="keypad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', '⌫'].map((k) => (
            <button
              type="button"
              key={k}
              className="chip chip--key"
              onClick={() => {
                if (k === '⌫') setDigits(digits.slice(0, -1));
                else if (digits.length < 6) setDigits((digits + k).replace(/^0+(?=\d)/, ''));
              }}
            >
              {k}
            </button>
          ))}
        </div>
      )}

      <div className="btn-row entry-row">
        <button
          type="button"
          className="chip chip--flat"
          onClick={() => (mode === 'quick' ? setAdds(adds.slice(0, -1)) : setDigits(digits.slice(0, -1)))}
          disabled={turnPoints === 0}
        >
          Undo entry
        </button>
        <button type="button" className="chip chip--flat" onClick={clearTurn} disabled={turnPoints === 0}>
          Clear
        </button>
      </div>

      <div className="btn-row action-row">
        <button type="button" className="btn btn--danger farkle-btn" onClick={farkle} disabled={stamped}>
          FARKLE
        </button>
        <button type="button" className="btn btn--primary" onClick={bank} disabled={!canBank || stamped}>
          BANK IT
        </button>
      </div>

      <div className="spacer" />
      <Eyebrow className="field-label">Up next</Eyebrow>
      <div className="standings">
        {others.map((p) => {
          const last = p.turns[p.turns.length - 1];
          return (
            <div
              key={p.i}
              className={cx('row', p.score === leadScore && p.score > 0 && 'row--lead')}
            >
              <span className="who">{p.name}</span>
              {last !== undefined && (
                <span className={cx('last', last === 0 && 'last--farkle')}>
                  {last === 0 ? 'farkle' : `+${fmt(last)}`}
                </span>
              )}
              <span className="score">{fmt(p.score)}</span>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
